// ===========================================
// Room Utilization Chart Component
// ===========================================
// Bar chart showing room usage rates with color-coded utilization

import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

import { RoomUtilization } from '../../api/reports.api';

// ===========================================
// TYPES
// ===========================================

interface RoomUtilizationChartProps {
  data: RoomUtilization[];
  loading?: boolean;
}

// ===========================================
// HELPERS
// ===========================================

// Get color based on utilization rate
const getUtilizationColor = (rate: number): string => {
  if (rate >= 75) return '#5cb399'; // Green - well utilized
  if (rate >= 50) return '#FFCE00'; // Yellow - moderate
  return '#ff4040'; // Red - underutilized
};

// Custom tooltip for room details
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Box
        sx={{
          bgcolor: 'background.paper',
          p: 1.5,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" fontWeight={600} gutterBottom>
          {data.roomName}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Location: {data.locationName}
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          Used: {data.usedHours.toFixed(1)}h / {data.totalAvailableHours.toFixed(1)}h
        </Typography>
        <Typography variant="caption" display="block" fontWeight={600} color={getUtilizationColor(data.utilizationRate)}>
          Utilization: {data.utilizationRate.toFixed(1)}%
        </Typography>
      </Box>
    );
  }
  return null;
};

// ===========================================
// COMPONENT
// ===========================================

export default function RoomUtilizationChart({ data, loading }: RoomUtilizationChartProps) {
  // Loading state
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Room Utilization
          </Typography>
          <Alert severity="info">
            No room utilization data available for the selected period.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Room Utilization
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Usage rates by room (Green: &gt;75%, Yellow: 50-75%, Red: &lt;50%)
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data} margin={{ top: 20, right: 10, left: 10, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
            <XAxis
              dataKey="roomName"
              angle={-45}
              textAnchor="end"
              height={80}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              label={{ value: 'Utilization (%)', angle: -90, position: 'insideLeft' }}
              domain={[0, 100]}
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="utilizationRate" radius={[4, 4, 0, 0]}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getUtilizationColor(entry.utilizationRate)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
